import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  getBrowserTimezone,
  isLegacyTimeValue,
  isWallClockTime,
  validateTimezone,
} from 'src/services/timezone-service';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Docs: https://day.js.org/docs/en/display/format
 */
export const formatStr = {
  dateTime: 'DD MMM YYYY h:mm a', // 17 Apr 2022 12:00 am
  date: 'DD MMM YYYY', // 17 Apr 2022
  time: 'h:mm a', // 12:00 am
  split: {
    dateTime: 'DD/MM/YYYY h:mm a', // 17/04/2022 12:00 am
    date: 'DD/MM/YYYY', // 17/04/2022
  },
  paramCase: {
    dateTime: 'DD-MM-YYYY h:mm a', // 17-04-2022 12:00 am
    date: 'DD-MM-YYYY', // 17-04-2022
  },
};

// ---------------------------------------------------------------------------
// Display timezone (ONE UI timezone context, spec §41)
//
// `SettingsProvider` calls `setDisplayTimezone()` whenever the admin changes the
// header selector, so every `fDate` / `fTime` / `fDateTime` call site in the app
// renders the same UTC instant in the selected zone — no per-screen conversion.
// ---------------------------------------------------------------------------

let displayTimezone = getBrowserTimezone();

export function setDisplayTimezone(tz) {
  displayTimezone = validateTimezone(tz) ? tz : getBrowserTimezone();
}

export function getDisplayTimezone() {
  return displayTimezone;
}

/** dayjs in the display zone (or an explicit zone). Zone-less input is treated as UTC. */
function inZone(date, tz) {
  const zone = validateTimezone(tz) ? tz : displayTimezone;
  return dayjs.utc(date).tz(zone);
}

/** True for a legacy `@db.Date` value (`YYYY-MM-DD` or `...T00:00:00.000Z`): a calendar date, never shifted. */
function isDateOnlyValue(value) {
  return typeof value === 'string' && (/^\d{4}-\d{2}-\d{2}$/.test(value) || /T00:00:00(\.000)?Z$/.test(value));
}

/** Today's date in the DISPLAY zone. */
export function today(format) {
  return inZone(new Date()).startOf('day').format(format);
}



/** output: 17 Apr 2022 12:00 am
 */
export function fDateTime(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? inZone(date).format(format ?? formatStr.dateTime) : 'Invalid time value';
}



/** output: 17 Apr 2022
 */
export function fDate(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  if (!isValid) return 'Invalid time value';
  // A date-only value is a calendar date: render it as stored, never shifted by a zone.
  if (isDateOnlyValue(date)) return dayjs.utc(date).format(format ?? formatStr.date);
  return inZone(date).format(format ?? formatStr.date);
}



/** output: 12:00 am
 */
export function fTime(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  if (!isValid) return 'Invalid time value';
  // Legacy @db.Time(0) values are a wall clock labelled UTC: render as-is.
  if (isLegacyTimeValue(date)) return dayjs.utc(date).format(format ?? formatStr.time);
  return inZone(date).format(format ?? formatStr.time);
}

/** output: 12:00 am — reads the stored UTC value directly (no local conversion)
 */
export function fTimeUTC(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs.utc(date).format(format ?? formatStr.time) : 'Invalid time value';
}

/** output: 17 Apr 2022 — reads the stored UTC value directly (no local conversion)
 */
export function fDateUTC(date, format) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs.utc(date).format(format ?? formatStr.date) : 'Invalid time value';
}



/**
 * output: 12:00 PM
 *
 * Handles the three shapes the API uses:
 *  - bare "09:00" (facility schedule rules, recurring rules): a wall clock — rendered as-is
 *  - legacy "1970-01-01T09:00:00.000Z" (@db.Time(0)): a wall clock labelled UTC — rendered as-is
 *  - a real instant ("2026-09-20T03:00:00.000Z", `start_at`): converted to the display zone
 * The old implementation treated the first two as UTC and shifted them by the
 * browser offset, which is how an entered 08:00 re-displayed as 16:00.
 */
export function fTimeOnly(timeValue, format) {
  if (!timeValue) {
    return null;
  }

  if (isWallClockTime(timeValue)) {
    const tempDate = dayjs.utc(`2000-01-01 ${timeValue}`);
    return tempDate.isValid() ? tempDate.format(format ?? formatStr.time) : 'Invalid time value';
  }

  if (!dayjs(timeValue).isValid()) {
    return 'Invalid time value';
  }

  if (isLegacyTimeValue(timeValue)) {
    return dayjs.utc(timeValue).format(format ?? formatStr.time);
  }

  return inZone(timeValue).format(format ?? formatStr.time);
}

/** output: 1713250100
 */
export function fTimestamp(date) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).valueOf() : 'Invalid time value';
}



/** output: a few seconds, 2 years
 */
export function fToNow(date) {
  if (!date) {
    return null;
  }

  const isValid = dayjs(date).isValid();

  return isValid ? dayjs(date).toNow(true) : 'Invalid time value';
}



/** output: boolean
 */
export function fIsBetween(inputDate, startDate, endDate) {
  if (!inputDate || !startDate || !endDate) {
    return false;
  }

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (formattedInputDate && formattedStartDate && formattedEndDate) {
    return formattedInputDate >= formattedStartDate && formattedInputDate <= formattedEndDate;
  }

  return false;
}



/** output: boolean
 */
export function fIsAfter(startDate, endDate) {
  return dayjs(startDate).isAfter(endDate);
}



/** output: boolean
 */
export function fIsSame(startDate, endDate, units) {
  if (!startDate || !endDate) {
    return false;
  }

  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  if (!isValid) {
    return 'Invalid time value';
  }

  return dayjs(startDate).isSame(endDate, units ?? 'year');
}


// ---------------------------------------------------------------------------
// Explicit-zone helpers. Prefer `src/services/date-time-service` in new code.
// ---------------------------------------------------------------------------

/** Display a UTC datetime in a specific IANA timezone.
 *  e.g. fTimeInTimezone('1970-01-01T00:00:00Z', 'Asia/Manila') → "8:00 am"
 */
export function fTimeInTimezone(date, tz, format) {
  if (!date || !tz) return fTime(date, format);
  const d = inZone(date, tz);
  return d.isValid() ? d.format(format ?? formatStr.time) : 'Invalid time value';
}

/** Display a UTC date in a specific IANA timezone.
 */
export function fDateInTimezone(date, tz, format) {
  if (!date || !tz) return fDate(date, format);
  const d = inZone(date, tz);
  return d.isValid() ? d.format(format ?? formatStr.date) : 'Invalid time value';
}



/** output:
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */
export function fDateRangeShortLabel(startDate, endDate, initial) {
  const isValid = dayjs(startDate).isValid() && dayjs(endDate).isValid();

  const isAfter = fIsAfter(startDate, endDate);

  if (!isValid || isAfter) {
    return 'Invalid time value';
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}) {
  const result = dayjs()
    .add(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}

/** output: '2024-05-28T05:55:31+00:00'
 */
export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0,
}) {
  const result = dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
      })
    )
    .format();

  return result;
}
